import {
    type ComponentType,
    createContext,
    useContext,
    useEffect,
    type FC
} from "react";
import type {
    EmptyPartContentProps,
    EmptyContextType,
    EmptyPartKind
} from "./type";
import {
    warnOnce
} from "../../utils/development";

export const EMPTY_PART_NAMES: Record<EmptyPartKind, string> = {
    description: "Description",
    content: "Content",
    header: "Header",
    empty: "Empty",
    media: "Media",
    title: "Title"
};

export const EmptyContext = createContext<EmptyContextType | undefined>(undefined);

export const useEmptyContext = ({
    part
}: {
    part: EmptyPartKind;
}): EmptyContextType | undefined => {
    const context = useContext(EmptyContext);

    useEffect(() => {
        if(context) {
            return;
        }

        warnOnce({
            message: `Empty.${EMPTY_PART_NAMES[part]}: must be rendered inside <Empty>.`
        });
    }, [
        context,
        part
    ]);

    return context;
};

export const withEmptyContext = <P extends object>(
    part: EmptyPartKind,
    Content: ComponentType<EmptyPartContentProps<P>>
): FC<P> & {
    emptyPart: EmptyPartKind;
} => {
    const EmptyPartBase: FC<P> = (props) => {
        const context = useEmptyContext({
            part
        });

        if(!context) {
            return null;
        }

        return <Content
            {...props}
            context={context}
        />;
    };

    EmptyPartBase.displayName = `Empty.${EMPTY_PART_NAMES[part]}`;

    return Object.assign(EmptyPartBase, {
        emptyPart: part
    });
};
